import JoditEditor, { Jodit } from 'jodit-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import 'jodit/es2015/jodit.min.css';

// ─── Ícones (Lucide) ────────────────────────────────────────────────────────────

/**
 * SVGs do Lucide registrados no Jodit para substituir os ícones padrão da toolbar,
 * alinhando-os ao design (traço fino, `viewBox` 24, `currentColor`).
 */
const LUCIDE_ICONS: Readonly<Record<string, string>> = {
  bold: '<path d="M6 12h9a4 4 0 0 1 0 8H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h7a4 4 0 0 1 0 8"/>',
  italic:
    '<line x1="19" x2="10" y1="4" y2="4"/><line x1="14" x2="5" y1="20" y2="20"/><line x1="15" x2="9" y1="4" y2="20"/>',
  underline: '<path d="M6 4v6a6 6 0 0 0 12 0V4"/><line x1="4" x2="20" y1="20" y2="20"/>',
  strikethrough:
    '<path d="M16 4H9a3 3 0 0 0-2.83 4"/><path d="M14 12a4 4 0 0 1 0 8H6"/><line x1="4" x2="20" y1="12" y2="12"/>',
  link: '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
  source: '<path d="m16 18 6-6-6-6"/><path d="m8 6-6 6 6 6"/>',
  ul: '<line x1="8" x2="21" y1="6" y2="6"/><line x1="8" x2="21" y1="12" y2="12"/><line x1="8" x2="21" y1="18" y2="18"/><line x1="3" x2="3.01" y1="6" y2="6"/><line x1="3" x2="3.01" y1="12" y2="12"/><line x1="3" x2="3.01" y1="18" y2="18"/>',
  ol: '<line x1="10" x2="21" y1="6" y2="6"/><line x1="10" x2="21" y1="12" y2="12"/><line x1="10" x2="21" y1="18" y2="18"/><path d="M4 6h1v4"/><path d="M4 10h2"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/>',
  image:
    '<rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>',
  table:
    '<path d="M12 3v18"/><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M3 9h18"/><path d="M3 15h18"/>',
};

for (const [name, body] of Object.entries(LUCIDE_ICONS)) {
  Jodit.modules.Icon.set(
    name,
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`,
  );
}

// ─── Destaque de células selecionadas ────────────────────────────────────────

/**
 * Classe aplicada às células de tabela selecionadas (ex.: ao arrastar para
 * mesclar). Estilizada em `globals.css`.
 */
export const SELECTED_CELL_CLASS = 'jodit-cell-selected';

/**
 * O Jodit destaca as células selecionadas injetando um `<style>` com seletores
 * por célula (`selectionCellStyle`). Esse mecanismo não produz efeito visível no
 * nosso setup, então patcheamos `addSelection`/`removeSelection` do módulo Table
 * para (des)marcar as células com a classe {@link SELECTED_CELL_CLASS}, que
 * controlamos via CSS próprio — abordagem confiável, igual ao registro de ícones
 * acima (muda o Jodit global que o `jodit-react` instancia).
 *
 * A classe é removida do HTML no `onChange` (ver `stripSelectionClass`) para não
 * vazar para o conteúdo salvo.
 */
type TableSelectionProto = {
  addSelection: (td: HTMLTableCellElement) => void;
  removeSelection: (td: HTMLTableCellElement) => void;
};

const CELL_HIGHLIGHT_PATCH = Symbol.for('mbx.jodit.cellHighlightPatched');

const tableProto = Jodit.modules.Table?.prototype as unknown as
  | (TableSelectionProto & Record<symbol, boolean>)
  | undefined;

if (tableProto && !tableProto[CELL_HIGHLIGHT_PATCH]) {
  tableProto[CELL_HIGHLIGHT_PATCH] = true;

  const originalAdd = tableProto.addSelection;
  const originalRemove = tableProto.removeSelection;

  tableProto.addSelection = function patchedAddSelection(this: unknown, td: HTMLTableCellElement) {
    originalAdd.call(this, td);
    td?.classList?.add(SELECTED_CELL_CLASS);
  };
  tableProto.removeSelection = function patchedRemoveSelection(
    this: unknown,
    td: HTMLTableCellElement,
  ) {
    originalRemove.call(this, td);
    td?.classList?.remove(SELECTED_CELL_CLASS);
  };
}

/**
 * Remove a classe de destaque de célula selecionada do HTML, evitando que ela
 * seja persistida no conteúdo salvo (a seleção pode continuar ativa no momento
 * em que o valor é lido).
 */
function stripSelectionClass(html: string): string {
  return html
    .replace(new RegExp(`\\s*\\b${SELECTED_CELL_CLASS}\\b`, 'g'), '')
    .replace(/\sclass=(["'])\s*\1/g, '');
}

// ─── i18n ─────────────────────────────────────────────────────────────────────

/**
 * Converte o locale do app (ex.: `pt-BR`, `en-US`, `es-ES`) para o código de
 * idioma reconhecido pelo Jodit (`pt_br`, `en`, `es`). O Jodit resolve o locale
 * por igualdade exata contra as chaves de `Jodit.lang`, então `pt-BR` não bate
 * com o pacote `pt_br` e cairia no fallback em inglês.
 */
function toJoditLanguage(locale: string): 'pt_br' | 'es' | 'en' {
  if (locale.startsWith('pt')) return 'pt_br';
  if (locale.startsWith('es')) return 'es';
  return 'en';
}

/**
 * Traduções para os rótulos de estilo das listas (ordenada/não ordenada).
 *
 * Esses rótulos vêm dos controles `ul`/`ol` do Jodit e não existem no pacote
 * `pt_br` (só em `en` e `es`), então precisam ser injetados via `config.i18n`.
 * Espanhol e inglês já traduzem essas chaves nativamente.
 */
const RICH_TEXT_I18N: Readonly<Record<string, Record<string, string>>> = {
  pt_br: {
    Default: 'Padrão',
    Circle: 'Círculo',
    Dot: 'Ponto',
    Quadrate: 'Quadrado',
    'Lower Alpha': 'Letra minúscula',
    'Lower Greek': 'Grego minúsculo',
    'Lower Roman': 'Romano minúsculo',
    'Upper Alpha': 'Letra maiúscula',
    'Upper Roman': 'Romano maiúsculo',
  },
};

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Props do componente RichTextEditor */
export interface RichTextEditorProps {
  /** Conteúdo HTML do editor */
  readonly value: string;
  /** Callback chamado ao perder foco — recebe o HTML atualizado */
  readonly onChange: (value: string) => void;
  /** Placeholder exibido quando o editor está vazio */
  readonly placeholder?: string;
  /** Desabilita a edição */
  readonly disabled?: boolean;
  /** Altura mínima da área de conteúdo em pixels */
  readonly minHeight?: number;
}

// ─── Utilitário ─────────────────────────────────────────────────────────────

/**
 * Verifica se o HTML do editor está efetivamente vazio (sem texto nem mídia).
 *
 * O Jodit representa o estado vazio de várias formas (`''`, `<p><br></p>`,
 * `&nbsp;`), então normalizamos antes de comparar.
 */
function isRichTextEmpty(html: string): boolean {
  if (!html) return true;
  // Qualquer mídia/tabela conta como conteúdo, mesmo sem texto.
  if (/<(img|video|audio|iframe|table|hr|input)\b/i.test(html)) return false;
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .trim();
  return text.length === 0;
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Editor rich text baseado em Jodit.
 *
 * Wrapper sobre `jodit-react` com configuração de toolbar e estilos
 * alinhados ao sistema de design ADS.
 *
 * Usa `onBlur` para atualizar o valor (evita re-renders a cada keystroke).
 *
 * @example
 * ```tsx
 * <RichTextEditor
 *   value={label}
 *   onChange={setLabel}
 *   minHeight={120}
 * />
 * ```
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled = false,
  minHeight = 120,
}: RichTextEditorProps) {
  const { i18n } = useTranslation();
  const language = toJoditLanguage(i18n.language);

  const config = useMemo(
    () => ({
      readonly: disabled,
      placeholder: placeholder ?? '',
      minHeight,
      language,
      i18n: RICH_TEXT_I18N,
      // Colagem: insere direto no Ctrl+V (sem o diálogo de confirmação do Jodit,
      // que ficava recortado pelo container e travava a colagem), limpando estilos
      // externos (Word/Docs/sites) mas mantendo tags básicas.
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      defaultActionOnPaste: 'insert_clear_html' as const,
      toolbar: true,
      buttons: [
        'bold',
        'italic',
        'underline',
        'strikethrough',
        '|',
        'ul',
        'ol',
        '|',
        'image',
        'table',
        'link',
        '|',
        'source',
      ],
      statusbar: false,
      toolbarAdaptive: false,
      toolbarSticky: false,
      toolbarStickyOffset: 0,
      allowResizeX: false,
      allowResizeY: false,
      showInvisibleChars: false,
      disablePlugins: ['add-new-line'],
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      style: { fontFamily: 'inherit', fontSize: 'inherit' },
    }),
    [disabled, placeholder, minHeight, language],
  );

  function handleBlur(newValue: string) {
    onChange(stripSelectionClass(newValue));
  }

  return (
    <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface [&_.jodit-container]:!border-none [&_.jodit-container]:!bg-transparent [&_.jodit-toolbar__box]:!bg-ctx-layout-body [&_.jodit-toolbar__box]:!border-b-hairline [&_.jodit-toolbar__box]:!border-ctx-layout-border [&_.jodit-toolbar]:!bg-ctx-layout-body [&_.jodit-toolbar-editor-collection]:!bg-ctx-layout-body [&_.jodit-toolbar-button]:!bg-transparent [&_.jodit-toolbar-button\_\_button]:!bg-transparent [&_.jodit-toolbar-button\_\_button]:!text-ctx-content-base [&_.jodit-toolbar-button\_\_button:hover]:!bg-ctx-layout-border [&_.jodit-toolbar-button.jodit-toolbar-button\_\_trigger]:!bg-transparent [&_.jodit-icon]:!h-mini [&_.jodit-icon]:!w-mini [&_.jodit-icon]:!fill-none [&_.jodit-icon]:!stroke-ctx-content-base [&_.jodit-workplace]:!bg-ctx-layout-surface [&_.jodit-wysiwyg]:!bg-ctx-layout-surface [&_.jodit-wysiwyg]:text-ctx-content-title [&_.jodit-wysiwyg]:text-xxs [&_.jodit-wysiwyg]:leading-lg [&_.jodit-placeholder]:text-ctx-content-placeholder [&_textarea]:!hidden [&_.jodit-source]:!hidden [&_.jodit-status-bar-link]:!hidden">
      {/*
        `key` alterna entre vazio/preenchido para forçar o Jodit a reinicializar
        nessas transições. Sem isso, ao receber o valor de forma programática
        (ex.: pré-preenchimento via API), o Jodit atualiza o conteúdo mas não
        esconde o placeholder — que ficava sobreposto ao texto. Como `value` só
        muda no blur, isso não remonta durante a digitação.
      */}
      <JoditEditor
        key={isRichTextEmpty(value) ? 'empty' : 'filled'}
        value={value}
        config={config}
        onBlur={handleBlur}
      />
    </div>
  );
}
